/* global __SEARCH_CONTEXT__ */
import {
    SearchEntryMethod,
    SearchEvents,
    SearchSelectionMethod,
    SearchSuggestionSource,
    trackSearchEvent,
} from 'Analytics/search-events';

const SEARCH_PATH = '/search/';

export default class SearchAutocomplete {
    constructor(searchFormElement, searchInputElement, searchClearElement, searchSubmitElement) {
        this.searchForm = searchFormElement;
        this.searchInput = searchInputElement;
        this.searchClear = searchClearElement;
        this.searchSubmit = searchSubmitElement;

        this.currentFocus = -1;
        this.suggestionsContainer = null;

        this._resetTrackingState();
    }

    _resetTrackingState() {
        this._focusedAt = null;
        this._keystrokeCount = 0;
        this._lastTypedText = '';
        this._suggestionsSnapshot = {
            suggestions_visible_count: 0,
            suggestions_count_recent: 0,
            suggestions_count_autocomplete: 0,
            had_autocomplete_results: false,
        };
        this._pendingEntryMethod = SearchEntryMethod.TYPED;
        this._pendingSelectionMethod = SearchSelectionMethod.TYPED_ONLY;
        this._pendingSuggestionPosition = null;
    }

    _timeSinceFocusMs() {
        return this._focusedAt === null ? null : Date.now() - this._focusedAt;
    }

    _isOnSearchPage() {
        return typeof window !== 'undefined' && window.location?.pathname === SEARCH_PATH;
    }

    initialize() {
        this.suggestionsContainer = this._createSuggestionsContainer(this.searchInput);

        this._initializeInputListener();
        this._initializeFocusListener();
        this._initializeClearListener();
        this._initializeKeyListener();
        this._initializeClickListener();
        this._initializeSubmitListener();
        this._syncSubmitVariant();
    }

    _debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }

    _initializeInputListener = () => {
        const debouncedOnClick = this._debounce((e) => this._on_click(e));
        this.searchInput.addEventListener('input', (e) => {
            this._pendingEntryMethod = SearchEntryMethod.TYPED;
            this._pendingSelectionMethod = SearchSelectionMethod.TYPED_ONLY;
            this._pendingSuggestionPosition = null;
            this._keystrokeCount += 1;
            this._lastTypedText = e.target.value;
            debouncedOnClick(e);
        });
    };

    _initializeClearListener = () => {
        this.searchClear.addEventListener('click', () => {
            const queryBeforeClear = this.searchInput.value;
            const hadDropdownOpen = !this.suggestionsContainer?.classList.contains('hidden');
            this.searchInput.value = '';
            this._hideXButton();
            this._syncSubmitVariant();
            this.searchInput.focus();
            trackSearchEvent(SearchEvents.BAR_CLEARED, {
                cleared_by: 'clear_button',
                had_dropdown_open: hadDropdownOpen,
                query_before_clear: queryBeforeClear,
                was_on_search_page: this._isOnSearchPage(),
            });
        });
    };

    _initializeFocusListener = () => {
        this.searchInput.addEventListener('focus', (e) => {
            this._focusedAt = Date.now();
            this._keystrokeCount = 0;
            this._on_click(e);
        });
    };

    _on_click(e) {
        this._closeAllLists();
        const { value } = e.target;

        this.currentFocus = -1;

        if (value.toLowerCase().length === 0) {
            this._hideXButton();
            this._displayRecentQueries();
        } else {
            this._showXButton();
            const encodedValue = encodeURIComponent(value.toLowerCase());
            this._displaySuggestions(encodedValue);
        }

        this._syncSubmitVariant();
    }

    _initializeKeyListener = () => {
        this.searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown') {
                this.currentFocus++;
                this._addActive();
            } else if (e.key === 'ArrowUp') {
                this.currentFocus--;
                this._addActive();
            } else if (e.key === 'Enter') {
                e.preventDefault();

                if (this.currentFocus > -1) {
                    const suggestionItems = this._getSuggestionItems();
                    this._pendingSelectionMethod = SearchSelectionMethod.KEYBOARD_ENTER;
                    suggestionItems?.[this.currentFocus].click();
                } else {
                    this._pendingEntryMethod = SearchEntryMethod.TYPED;
                    this._pendingSelectionMethod = SearchSelectionMethod.TYPED_ONLY;
                    this._pendingSuggestionPosition = null;
                    this.searchForm.requestSubmit();
                }
            }
        });
    };

    _initializeClickListener = () => {
        document.addEventListener('click', (e) => this._closeAllLists(e.target));
    };

    _initializeSubmitListener = () => {
        this.searchForm.addEventListener('submit', (e) => {
            const query = this.searchInput.value.trim();
            if (query.length === 0) {
                e.preventDefault();
                return;
            }
            this._persistForceHybridParam();
            trackSearchEvent(SearchEvents.SUBMITTED, {
                query,
                query_length: query.length,
                entry_method: this._pendingEntryMethod,
                selection_method: this._pendingSelectionMethod,
                suggestion_position: this._pendingSuggestionPosition,
                time_since_focus_ms: this._timeSinceFocusMs(),
                keystrokes_before_submit: this._keystrokeCount,
                ...this._suggestionsSnapshot,
            });
        });
    };

    _persistForceHybridParam = () => {
        const currentParams = new URLSearchParams(window.location.search);
        const forceHybrid = currentParams.get('force_hybrid');
        if (forceHybrid && !this.searchForm.querySelector('input[name="force_hybrid"]')) {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'force_hybrid';
            hidden.value = forceHybrid;
            this.searchForm.appendChild(hidden);
        }
    };

    _displayRecentQueries = () => {
        const { recentQueryURL } = __SEARCH_CONTEXT__;
        const recentQueriesEndpoint = `${recentQueryURL}`;

        $.get({ url: recentQueriesEndpoint }).then((data) => {
            const { recent_queries: recentQueries } = data;

            this._suggestionsSnapshot = {
                suggestions_visible_count: recentQueries.length,
                suggestions_count_recent: recentQueries.length,
                suggestions_count_autocomplete: 0,
                had_autocomplete_results: false,
            };

            if (recentQueries.length) {
                recentQueries.forEach((query, position) => {
                    const suggestionItem = this._createSuggestion(
                        query,
                        '',
                        'fa-clock-o',
                        SearchSuggestionSource.RECENT_HISTORY,
                        position,
                    );
                    this.suggestionsContainer?.appendChild(suggestionItem);
                });

                this.suggestionsContainer?.classList.remove('hidden');
            }
        });
    };

    _retrieveSuggestions = (val) => {
        return new Promise((resolve) => {
            const { suggestionsQueryURL } = __SEARCH_CONTEXT__;
            const suggestionsEndpoint = `${suggestionsQueryURL}?prefix=${val}`;

            $.get({ url: suggestionsEndpoint }).then((data) => {
                const { suggestions } = data;

                resolve(suggestions);
            });
        });
    };

    _getSuggestionItems = () => {
        return this.suggestionsContainer?.querySelectorAll('.autocomplete__item');
    };

    _displaySuggestions = async (val) => {
        const suggestions = await this._retrieveSuggestions(val);

        this._suggestionsSnapshot = {
            suggestions_visible_count: suggestions.length,
            suggestions_count_recent: 0,
            suggestions_count_autocomplete: suggestions.length,
            had_autocomplete_results: suggestions.length > 0,
        };

        if (suggestions.length) {
            const typedText = decodeURIComponent(val);
            suggestions.forEach((suggestion, position) => {
                const suggestionItem = this._createSuggestion(
                    suggestion,
                    typedText,
                    'fa-search',
                    SearchSuggestionSource.AUTOCOMPLETE,
                    position,
                );
                this.suggestionsContainer?.appendChild(suggestionItem);
            });

            this.suggestionsContainer?.classList.remove('hidden');
        }
    };

    _createSuggestionsContainer = (inputElement) => {
        const { id, parentNode } = inputElement;
        const suggestionsContainer = document.createElement('div');

        suggestionsContainer.setAttribute('id', this._formatAutocompleteListId(id));
        suggestionsContainer.classList.add('autocomplete__items', 'hidden');
        parentNode.appendChild(suggestionsContainer);

        if (this.useAI) {
            const aiSuggestionsTitleContainer = document.createElement('div');

            aiSuggestionsTitleContainer.innerText = 'AI SUGGESTED';
            aiSuggestionsTitleContainer.classList.add('autocomplete__items-title', 'hidden');
            suggestionsContainer.appendChild(aiSuggestionsTitleContainer);
        }

        return suggestionsContainer;
    };

    _createSuggestion = (suggestion, val, iconClass, source, position) => {
        const suggestionItem = document.createElement('div');
        suggestionItem.classList.add('autocomplete__item');
        suggestionItem.dataset.suggestionSource = source;
        suggestionItem.dataset.suggestionPosition = String(position);

        const suggestionItemIcon = document.createElement('span');
        suggestionItemIcon.setAttribute('class', `fa ${iconClass} autocomplete__item-icon`);
        suggestionItem.appendChild(suggestionItemIcon);

        const suggestionItemText = document.createElement('span');
        suggestionItemText.innerHTML = `<span style="color:#212529">${suggestion.substr(
            0,
            val.length,
        )}</span>${suggestion.substr(val.length)}`;
        suggestionItem.appendChild(suggestionItemText);
        suggestionItem.addEventListener('click', () => {
            if (this.searchSubmit?.loading) return;

            const selectionMethod =
                this._pendingSelectionMethod === SearchSelectionMethod.KEYBOARD_ENTER
                    ? SearchSelectionMethod.KEYBOARD_ENTER
                    : SearchSelectionMethod.MOUSE;
            trackSearchEvent(SearchEvents.SUGGESTION_CLICKED, {
                suggestion_text: suggestion,
                suggestion_source: source,
                suggestion_position: position,
                typed_text: val,
                typed_text_length: val.length,
                time_since_focus_ms: this._timeSinceFocusMs(),
                selection_method: selectionMethod,
                ...this._suggestionsSnapshot,
            });
            this._pendingEntryMethod =
                source === SearchSuggestionSource.RECENT_HISTORY
                    ? SearchEntryMethod.RECENT_HISTORY
                    : SearchEntryMethod.AUTOCOMPLETE;
            this._pendingSelectionMethod = selectionMethod;
            this._pendingSuggestionPosition = position;
            this._closeAllLists();
            this.searchInput.value = suggestion;
            this.searchForm.requestSubmit();
        });

        return suggestionItem;
    };

    _formatAutocompleteListId = (id) => `${id}-autocomplete-list`;

    _addActive = () => {
        const suggestionItems = this._getSuggestionItems();

        if (!suggestionItems) {
            return;
        }

        [...suggestionItems].forEach((el) => el.classList.remove('active'));

        if (this.currentFocus >= suggestionItems.length) {
            this.currentFocus = 0;
        }
        if (this.currentFocus < 0) {
            this.currentFocus = suggestionItems.length - 1;
        }

        suggestionItems[this.currentFocus].classList.add('active');
    };

    _closeAllLists = (element) => {
        if (element === this.searchInput || !this.suggestionsContainer) {
            return;
        }

        const items = document.querySelectorAll(`#${this.suggestionsContainer.id} .autocomplete__item`);
        const listParent = items.length ? items[0].parentNode : null;

        for (let i = 0; i < items.length; i++) {
            if (element !== items[i]) {
                items[i].parentNode.removeChild(items[i]);
            }
        }

        listParent?.classList.add('hidden');
    };

    _showXButton() {
        this.searchClear.classList.remove('hidden');
    }

    _hideXButton() {
        this.searchClear.classList.add('hidden');
    }

    _syncSubmitVariant() {
        const hasQuery = this.searchInput.value.length > 0;
        this.searchSubmit?.setAttribute('variant', hasQuery ? 'solid' : 'ghost');
        this.searchSubmit?.setAttribute('color-scheme', hasQuery ? 'primary' : 'neutral');
    }
}
